import { useLayoutEffect, useMemo, useState } from "react";

import { isSameDay, isWithinInterval } from "date-fns";

import { parseDateAndTime } from "@/utils/datetime";

interface Appointment {
  id?: string;
  status?: string | null;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
}

export default function useAppointmentCurrent(
  appointment: Appointment | undefined
): boolean {
  const [isCurrent, setIsCurrent] = useState(false);

  const status = useMemo(
    () => appointment?.status ?? "finished",
    [appointment]
  );
  const startDateTime = useMemo(
    () =>
      parseDateAndTime(
        appointment?.startDate ?? new Date(),
        appointment?.startTime ?? new Date()
      ),
    [appointment]
  );
  const endDateTime = useMemo(
    () =>
      parseDateAndTime(
        appointment?.endDate ?? new Date(),
        appointment?.endTime ?? new Date()
      ),
    [appointment]
  );

  useLayoutEffect(() => {
    try {
      setIsCurrent(_isCurrent(status, startDateTime, endDateTime));
    } catch (e) {
      console.warn("[useAppointmentCurrent] Invalid interval:", {
        appointmentId: appointment?.id,
        start: startDateTime,
        end: endDateTime,
      });
      return;
    }

    // Dont run the interval if it is not even the current date.
    if (!isSameDay(startDateTime, new Date())) return;

    const timer = setInterval(
      () => setIsCurrent(_isCurrent(status, startDateTime, endDateTime)),
      1000
    );

    return () => clearInterval(timer);
  }, [appointment]);

  return isCurrent;
}

function _isCurrent(
  status: string,
  startDateTime: Date,
  endDateTime: Date
): boolean {
  if (status !== "upcoming") return false;

  return isWithinInterval(new Date(), {
    start: startDateTime,
    end: endDateTime,
  });
}
